'use client';
import { DEFAULT_MEETING_CONFIG } from '@govinity/shared-config/governments';
import { computeVoteOutcome } from '@govinity/validation';
import { Check, Gavel, Users, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { RichTextDisplay } from '../ui/rich-text-display';
import { Separator } from '../ui/separator';
import { StatusBadge } from '../ui/status-badge';
/**
 * Counts votes by response type.
 */
function getMotionVoteCounts(votes) {
    var counts = {
        abstain: 0,
        absent: 0,
        aye: 0,
        nay: 0,
        recuse: 0,
    };
    for (var _i = 0, votes_1 = votes; _i < votes_1.length; _i++) {
        var vote = votes_1[_i].vote;
        counts[vote]++;
    }
    return counts;
}
/**
 * Groups votes by response type.
 */
function groupMotionVotes(votes) {
    var groups = {
        abstain: [],
        absent: [],
        aye: [],
        nay: [],
        recuse: [],
    };
    for (var _i = 0, votes_2 = votes; _i < votes_2.length; _i++) {
        var vote = votes_2[_i];
        groups[vote.vote].push(vote);
    }
    return groups;
}
/**
 * Displays a single motion record with optional vote display.
 */
export function MinutesMotionCard(_a) {
    var _b, _c, _d;
    var motion = _a.motion, onVoteClick = _a.onVoteClick, _e = _a.showVote, showVote = _e === void 0 ? false : _e, _f = _a.voteLabels, voteLabels = _f === void 0 ? DEFAULT_MEETING_CONFIG.votes : _f;
    var legislativeAction = motion.legislativeAction, mover = motion.mover, motionText = motion.motionText, resultLegislativeStatus = motion.resultLegislativeStatus, seconder = motion.seconder, votes = motion.votes;
    var outcome = (_b = resultLegislativeStatus === null || resultLegislativeStatus === void 0 ? void 0 : resultLegislativeStatus.outcome) !== null && _b !== void 0 ? _b : (votes.length > 0 ? computeVoteOutcome(votes, legislativeAction === null || legislativeAction === void 0 ? void 0 : legislativeAction.tiebreakRule) : null);
    var voteCounts = votes.length > 0 ? getMotionVoteCounts(votes) : null;
    var isSubsidiary = motion.parentMotionId != null;
    var canVote = motion.userCanVote && motion.votingOpen && onVoteClick !== undefined;
    var userVoteLabel = motion.userVote ? voteLabels[motion.userVote] : null;
    var takesSecond = (_c = legislativeAction === null || legislativeAction === void 0 ? void 0 : legislativeAction.isSecondRequired) !== null && _c !== void 0 ? _c : true;
    var takesVote = (_d = legislativeAction === null || legislativeAction === void 0 ? void 0 : legislativeAction.isVoteRequired) !== null && _d !== void 0 ? _d : true;
    return (<Card className="border-primary rounded-sm border-l-4 py-2">
      <CardContent className="p-2 pb-0 pl-4">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Gavel className="text-muted-foreground h-4 w-4" aria-hidden="true"/>
            <span className="text-muted-foreground text-xs font-medium uppercase">
              {isSubsidiary ? 'Subsidiary Motion' : 'Motion'}
            </span>
            {legislativeAction && (<Badge variant="secondary" className="text-xs">
                {legislativeAction.name}
              </Badge>)}
            {motion.votingOpen && (<StatusBadge variant="info" className="text-xs">
                Voting in progress
              </StatusBadge>)}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {userVoteLabel && (<Badge variant="secondary" className="text-xs">
                Your vote: {userVoteLabel}
              </Badge>)}
            {canVote && !!onVoteClick && (<Button aria-label={motion.userVote ? 'Change your vote on this motion' : 'Vote on this motion'} onClick={function () { return onVoteClick(motion); }} size="xs" type="button" variant={motion.userVote ? 'outline' : 'default'}>
                {motion.userVote ? 'Change Vote' : 'Vote'}
              </Button>)}
          </div>
        </div>

        {motionText && <RichTextDisplay className="prose-sm mb-3" content={motionText}/>}

        {takesSecond && (<div className="text-muted-foreground space-y-1 text-sm">
            {mover && (<p>
                <span className="font-medium">Mover:</span> {mover.firstName} {mover.lastName}
                {mover.position && (<span className="text-muted-foreground"> ({mover.position})</span>)}
              </p>)}
            {seconder && (<p>
                <span className="font-medium">Seconder:</span> {seconder.firstName}{' '}
                {seconder.lastName}
                {seconder.position && (<span className="text-muted-foreground"> ({seconder.position})</span>)}
              </p>)}
          </div>)}

        {!takesVote && resultLegislativeStatus && (<>
            <Separator className="my-3"/>
            <Badge variant="secondary" className="text-xs">
              <span className="sr-only">Result: </span>
              {resultLegislativeStatus.name}
            </Badge>
          </>)}

        {showVote && voteCounts && (<>
            <Separator className="my-3"/>
            <div className="flex items-center gap-3">
              {outcome === 'pass' && (<Badge variant="success" className="gap-1">
                  <Check className="h-3 w-3" aria-hidden="true"/>
                  Passed
                </Badge>)}
              {outcome === 'fail' && (<Badge variant="destructive" className="gap-1">
                  <X className="h-3 w-3" aria-hidden="true"/>
                  Failed
                </Badge>)}
              <div className="text-muted-foreground flex gap-2 text-xs">
                <span>
                  <span className="text-success-foreground font-medium">{voteCounts.aye}</span>{' '}
                  {voteLabels.aye}
                </span>
                <span>
                  <span className="text-danger-foreground font-medium">{voteCounts.nay}</span>{' '}
                  {voteLabels.nay}
                </span>
                {voteCounts.abstain > 0 && (<span>
                    <span className="font-medium">{voteCounts.abstain}</span> {voteLabels.abstain}
                  </span>)}
                {voteCounts.recuse > 0 && (<span>
                    <span className="font-medium">{voteCounts.recuse}</span> {voteLabels.recuse}
                  </span>)}
                {voteCounts.absent > 0 && (<span>
                    <span className="font-medium">{voteCounts.absent}</span> {voteLabels.absent}
                  </span>)}
              </div>
            </div>
          </>)}
      </CardContent>
    </Card>);
}
/**
 * Displays vote results for a motion.
 */
export function MinutesVoteCard(_a) {
    var _b;
    var motion = _a.motion, _c = _a.voteLabels, voteLabels = _c === void 0 ? DEFAULT_MEETING_CONFIG.votes : _c;
    var legislativeAction = motion.legislativeAction, resultLegislativeStatus = motion.resultLegislativeStatus, votes = motion.votes;
    if (votes.length === 0)
        return null;
    var outcome = (_b = resultLegislativeStatus === null || resultLegislativeStatus === void 0 ? void 0 : resultLegislativeStatus.outcome) !== null && _b !== void 0 ? _b : computeVoteOutcome(votes, legislativeAction === null || legislativeAction === void 0 ? void 0 : legislativeAction.tiebreakRule);
    var groupedVotes = groupMotionVotes(votes);
    return (<Card className="border-primary rounded-sm border-l-4 py-2">
      <CardContent className="p-2 pb-0 pl-4">
        <div className="mb-3 flex items-center gap-2">
          <Users className="text-muted-foreground h-4 w-4" aria-hidden="true"/>
          <span className="text-muted-foreground text-xs font-medium uppercase">Vote</span>
          {outcome === 'pass' && (<Badge variant="success" className="gap-1">
              <Check className="h-3 w-3" aria-hidden="true"/>
              Passed
            </Badge>)}
          {outcome === 'fail' && (<Badge variant="destructive" className="gap-1">
              <X className="h-3 w-3" aria-hidden="true"/>
              Failed
            </Badge>)}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {groupedVotes.aye.length > 0 && (<div>
              <h4 className="text-success-foreground mb-1 text-xs font-medium uppercase">
                {voteLabels.aye} ({groupedVotes.aye.length})
              </h4>
              <ul className="text-muted-foreground space-y-0.5 text-sm">
                {groupedVotes.aye.map(function (_a) {
                var meetingMember = _a.meetingMember;
                return (<li key={meetingMember.id}>
                    {meetingMember.firstName} {meetingMember.lastName}
                  </li>);
            })}
              </ul>
            </div>)}
          {groupedVotes.nay.length > 0 && (<div>
              <h4 className="text-danger-foreground mb-1 text-xs font-medium uppercase">
                {voteLabels.nay} ({groupedVotes.nay.length})
              </h4>
              <ul className="text-muted-foreground space-y-0.5 text-sm">
                {groupedVotes.nay.map(function (_a) {
                var meetingMember = _a.meetingMember;
                return (<li key={meetingMember.id}>
                    {meetingMember.firstName} {meetingMember.lastName}
                  </li>);
            })}
              </ul>
            </div>)}
          {groupedVotes.abstain.length > 0 && (<div>
              <h4 className="text-muted-foreground mb-1 text-xs font-medium uppercase">
                {voteLabels.abstain} ({groupedVotes.abstain.length})
              </h4>
              <ul className="text-muted-foreground space-y-0.5 text-sm">
                {groupedVotes.abstain.map(function (_a) {
                var meetingMember = _a.meetingMember;
                return (<li key={meetingMember.id}>
                    {meetingMember.firstName} {meetingMember.lastName}
                  </li>);
            })}
              </ul>
            </div>)}
          {groupedVotes.recuse.length > 0 && (<div>
              <h4 className="text-muted-foreground mb-1 text-xs font-medium uppercase">
                {voteLabels.recuse} ({groupedVotes.recuse.length})
              </h4>
              <ul className="text-muted-foreground space-y-0.5 text-sm">
                {groupedVotes.recuse.map(function (_a) {
                var meetingMember = _a.meetingMember;
                return (<li key={meetingMember.id}>
                    {meetingMember.firstName} {meetingMember.lastName}
                  </li>);
            })}
              </ul>
            </div>)}
        </div>
      </CardContent>
    </Card>);
}
/**
 * Displays a roll call record.
 */
export function MinutesRollCallCard(_a) {
    var rollCallGroup = _a.rollCallGroup;
    var rollCalls = rollCallGroup.rollCalls;
    var statusMap = new Map();
    for (var _i = 0, rollCalls_1 = rollCalls; _i < rollCalls_1.length; _i++) {
        var rollCall = rollCalls_1[_i];
        for (var _b = 0, _c = rollCall.statuses; _b < _c.length; _b++) {
            var status_1 = _c[_b];
            var existing = statusMap.get(status_1.meetingMemberId);
            if (!existing || new Date(status_1.createdAt) > new Date(existing.createdAt)) {
                statusMap.set(status_1.meetingMemberId, status_1);
            }
        }
    }
    var grouped = {
        absent: [],
        present: [],
    };
    for (var _d = 0, _e = Array.from(statusMap.values()); _d < _e.length; _d++) {
        var status_2 = _e[_d];
        if (status_2.isPresent) {
            grouped.present.push(status_2);
        }
        else {
            grouped.absent.push(status_2);
        }
    }
    return (<Card className="border-primary rounded-sm border-l-4 py-2">
      <CardContent className="p-2 pb-0 pl-4">
        <div className="mb-3 flex items-center gap-2">
          <Users className="text-muted-foreground h-4 w-4" aria-hidden="true"/>
          <span className="text-muted-foreground text-xs font-medium uppercase">Roll Call</span>
          {rollCallGroup.isRecorded && (<Badge variant="secondary" className="text-xs">
              Recorded
            </Badge>)}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {grouped.present.length > 0 && (<div>
              <h4 className="text-success-foreground mb-1 text-xs font-medium uppercase">
                Present ({grouped.present.length})
              </h4>
              <ul className="text-muted-foreground space-y-0.5 text-sm">
                {grouped.present.map(function (status) { return (<li key={status.meetingMemberId} className="flex items-center gap-1">
                    {status.meetingMember.firstName} {status.meetingMember.lastName}
                    {status.isPresencePhysical === false && (<span className="text-muted-foreground text-xs">(Remote)</span>)}
                  </li>); })}
              </ul>
            </div>)}
          {grouped.absent.length > 0 && (<div>
              <h4 className="text-danger-foreground mb-1 text-xs font-medium uppercase">
                Absent ({grouped.absent.length})
              </h4>
              <ul className="text-muted-foreground space-y-0.5 text-sm">
                {grouped.absent.map(function (status) { return (<li key={status.meetingMemberId} className="flex items-center gap-1">
                    {status.meetingMember.firstName} {status.meetingMember.lastName}
                    {status.isAbsenceExcused && (<span className="text-muted-foreground text-xs">(Excused)</span>)}
                  </li>); })}
              </ul>
            </div>)}
        </div>
      </CardContent>
    </Card>);
}
/**
 * Renders the appropriate minutes record card based on record type.
 */
export function MinutesItemRecordCard(_a) {
    var onVoteClick = _a.onVoteClick, record = _a.record, voteLabels = _a.voteLabels;
    switch (record.type) {
        case 'motion':
            return (<MinutesMotionCard motion={record.record} onVoteClick={onVoteClick} voteLabels={voteLabels}/>);
        case 'rollCall':
            return <MinutesRollCallCard rollCallGroup={record.record}/>;
        case 'vote':
            return <MinutesVoteCard motion={record.record} voteLabels={voteLabels}/>;
    }
}
